import {
  Badge,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { OffersWidgetData } from '@texas/api/endpoints/offerApi';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useTranslation } from 'react-i18next';

interface Props {
  data: OffersWidgetData | null;
}

const statusColors: Record<string, string> = {
  sent: 'green',
  unfinished: 'orange',
};

export function OffersTable({ data }: Props) {
  const { t } = useTranslation();
  const rows = [
    ...(data?.offeredOffers ?? []).map((offeredOffer) => ({
      id: `offeredOffer-${offeredOffer.id}`,
      offerGroupId: offeredOffer.offerGroupId,
      articleId: offeredOffer.articleId,
      variantId: offeredOffer.variantId,
      label: offeredOffer.offerNumber,
      customerName: offeredOffer.customerName,
      date: offeredOffer.sentOn,
      branchIdentifiers: offeredOffer.branches.map(
        (branch) => branch.branchIdentifier,
      ),
      tierPriceCount: offeredOffer.tierPriceCount,
      status: 'sent' as const,
    })),
    ...(data?.unfinishedOfferGroups ?? []).map((group) => ({
      id: `unfinished-${group.offerGroupId}`,
      offerGroupId: group.offerGroupId,
      articleId: group.articleId,
      variantId: group.variantId,
      label: t('offer.unfinished'),
      customerName: group.customerName,
      date: group.created,
      branchIdentifiers: group.branchIdentifiers,
      tierPriceCount: group.tierPriceCount,
      status: 'unfinished' as const,
    })),
  ];

  return (
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th>{t('offer.offer')}</Th>
          <Th>{t('general.customer')}</Th>
          <Th>{t('general.branches')}</Th>
          <Th>{t('offer.prices')}</Th>
          <Th>{t('article.created')}</Th>
          <Th>{t('general.status')}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>
              <AngularNavLink
                to="variant.offers.group"
                navobj={{
                  articleId: row.articleId,
                  variantId: row.variantId,
                  offerGroupId: row.offerGroupId,
                }}
              >
                {row.label}
              </AngularNavLink>
            </Td>
            <Td>
              <Text fontSize="sm">{row.customerName}</Text>
            </Td>
            <Td>
              <Flex gap={1} wrap="wrap">
                {row.branchIdentifiers.map((branchIdentifier) => (
                  <Badge key={branchIdentifier} colorScheme="gray">
                    {branchIdentifier}
                  </Badge>
                ))}
              </Flex>
            </Td>
            <Td>
              <Text fontSize="sm">{row.tierPriceCount}</Text>
            </Td>
            <Td>
              <Text fontSize="sm">{formatDate(Locale.En, row.date)}</Text>
            </Td>
            <Td>
              <Badge colorScheme={statusColors[row.status]}>
                {row.status === 'sent'
                  ? t('offer.sent')
                  : t('offer.unfinished')}
              </Badge>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
